import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Alert, Spinner, Modal, Tabs, Tab, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const Reports = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalSocios: 0,
    totalAdmins: 0,
    activeUsers: 0
  });
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedSocio, setSelectedSocio] = useState(null);
  const [socioDetails, setSocioDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    if (!hasPermission('reports', 'view')) {
      toast.error('No tienes permisos para ver reportes');
      return;
    }
    fetchReportData();
  }, [hasPermission]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users?limit=1000');
      const usersData = response.data.users || [];
      
      setUsers(usersData);
      
      // Calcular estadísticas
      const totalUsers = usersData.length;
      const totalSocios = usersData.filter(user => user.role === 'socio').length;
      const totalAdmins = usersData.filter(user => ['admin', 'super-admin'].includes(user.role)).length;
      const activeUsers = usersData.filter(user => user.isActive).length;
      
      setStats({
        totalUsers,
        totalSocios,
        totalAdmins,
        activeUsers
      });
      
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Error al cargar datos del reporte');
    } finally {
      setLoading(false);
    }
  };

  const fetchSocioDetails = async (socioId) => {
    try {
      setLoadingDetails(true);
      const [registrosRes, prestamosRes] = await Promise.all([
        axios.get(`/api/registros-mensuales/socio/${socioId}`),
        axios.get(`/api/solicitudes-prestamo/socio/${socioId}`)
      ]);
      
      setSocioDetails({
        registrosMensuales: registrosRes.data.data || [],
        prestamos: prestamosRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching socio details:', error);
      toast.error('Error al cargar detalles del socio');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewSocio = async (user) => {
    setSelectedSocio(user);
    setShowModal(true);
    await fetchSocioDetails(user._id);
  };

  const formatCurrency = (num) => {
    return parseFloat(num || 0).toLocaleString('es-ES', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getNombreMes = (mes) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return meses[mes - 1] || '';
  };

  const exportToCSV = () => {
    if (!hasPermission('reports', 'export')) {
      toast.error('No tienes permisos para exportar reportes');
      return;
    }

    const csvContent = [
      ['Nombre', 'Email', 'Rol', 'Estado', 'Fecha Creación'],
      ...users.map(user => [
        user.name,
        user.email,
        user.role,
        user.isActive ? 'Activo' : 'Inactivo',
        new Date(user.createdAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `reporte_usuarios_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Reporte exportado exitosamente');
  };

  if (!hasPermission('reports', 'view')) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          No tienes permisos para acceder a esta página.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2>
              <i className="fas fa-chart-bar me-2"></i>
              Reportes del Sistema
            </h2>
            {hasPermission('reports', 'export') && (
              <Button variant="success" onClick={exportToCSV}>
                <i className="fas fa-download me-2"></i>
                Exportar CSV
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Estadísticas */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <i className="fas fa-users fa-3x text-primary mb-3"></i>
              <h3 className="text-primary">{stats.totalUsers}</h3>
              <p className="text-muted mb-0">Total Usuarios</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <i className="fas fa-user-friends fa-3x text-success mb-3"></i>
              <h3 className="text-success">{stats.totalSocios}</h3>
              <p className="text-muted mb-0">Total Socios</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <i className="fas fa-user-shield fa-3x text-warning mb-3"></i>
              <h3 className="text-warning">{stats.totalAdmins}</h3>
              <p className="text-muted mb-0">Administradores</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <i className="fas fa-user-check fa-3x text-info mb-3"></i>
              <h3 className="text-info">{stats.activeUsers}</h3>
              <p className="text-muted mb-0">Usuarios Activos</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabla de usuarios */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-table me-2"></i>
                Detalle de Usuarios
              </h5>
            </Card.Header>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Estado</th>
                    <th>Fecha Registro</th>
                    <th>Última Actividad</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user._id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <i className="fas fa-user-circle fa-2x text-muted me-2"></i>
                          <div>
                            <div className="fw-bold">{user.name}</div>
                            {user.role === 'socio' && user.socioInfo?.numeroSocio && (
                              <small className="text-muted">
                                Socio: {user.socioInfo.numeroSocio}
                              </small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`badge ${
                          user.role === 'super-admin' ? 'bg-danger' :
                          user.role === 'admin' ? 'bg-warning' :
                          user.role === 'sub-admin' ? 'bg-info' :
                          'bg-success'
                        }`}>
                          {user.role === 'super-admin' ? 'Super Admin' :
                           user.role === 'admin' ? 'Admin' :
                           user.role === 'sub-admin' ? 'Sub-Admin' :
                           'Socio'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${user.isActive ? 'bg-success' : 'bg-danger'}`}>
                          {user.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>
                        {user.lastLogin ? 
                          new Date(user.lastLogin).toLocaleDateString() : 
                          'Nunca'
                        }
                      </td>
                      <td>
                        {user.role === 'socio' && (
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="btn-sm"
                            title="Ver detalles del socio"
                            onClick={() => handleViewSocio(user)}
                          >
                            <i className="fas fa-eye me-1"></i>
                            Ver
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de detalles del socio */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-user me-2"></i>
            Detalles de {selectedSocio?.name}
            {selectedSocio?.socioInfo?.numeroSocio && (
              <small className="text-muted ms-2">
                (Socio #{selectedSocio.socioInfo.numeroSocio})
              </small>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" /> Cargando información...
            </div>
          ) : (
            <Tabs defaultActiveKey="aportaciones" className="mb-3">
              {/* Pestaña Aportaciones */}
              <Tab eventKey="aportaciones" title="💰 Aportaciones">
                {socioDetails?.registrosMensuales && socioDetails.registrosMensuales.length > 0 ? (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead className="table-primary">
                        <tr>
                          <th>Mes</th>
                          <th>Año</th>
                          <th>Monto Aportado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {socioDetails.registrosMensuales
                          .filter(r => r.aportes > 0)
                          .map((registro, idx) => (
                            <tr key={idx}>
                              <td><strong>{getNombreMes(registro.mes)}</strong></td>
                              <td className="text-center">{registro.año}</td>
                              <td className="text-end text-success">
                                <strong>{formatCurrency(registro.aportes)}</strong>
                              </td>
                            </tr>
                          ))}
                        <tr className="table-light">
                          <td colSpan="2" className="fw-bold">Total Aportaciones:</td>
                          <td className="text-end text-success fw-bold">
                            {formatCurrency(
                              socioDetails.registrosMensuales.reduce((sum, r) => sum + (r.aportes || 0), 0)
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="info">No hay aportaciones registradas</Alert>
                )}
              </Tab>

              {/* Pestaña Paseos */}
              <Tab eventKey="paseos" title="👥 Paseos">
                {socioDetails?.registrosMensuales && socioDetails.registrosMensuales.length > 0 ? (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead className="table-info">
                        <tr>
                          <th>Mes</th>
                          <th>Año</th>
                          <th>Monto Paseo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {socioDetails.registrosMensuales
                          .filter(r => r.paseo > 0)
                          .map((registro, idx) => (
                            <tr key={idx}>
                              <td><strong>{getNombreMes(registro.mes)}</strong></td>
                              <td className="text-center">{registro.año}</td>
                              <td className="text-end text-info">
                                <strong>{formatCurrency(registro.paseo)}</strong>
                              </td>
                            </tr>
                          ))}
                        <tr className="table-light">
                          <td colSpan="2" className="fw-bold">Total Paseos:</td>
                          <td className="text-end text-info fw-bold">
                            {formatCurrency(
                              socioDetails.registrosMensuales.reduce((sum, r) => sum + (r.paseo || 0), 0)
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="info">No hay paseos registrados</Alert>
                )}
              </Tab>

              {/* Pestaña Multas */}
              <Tab eventKey="multas" title="⚖️ Multas">
                {socioDetails?.registrosMensuales && socioDetails.registrosMensuales.length > 0 ? (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead className="table-danger">
                        <tr>
                          <th>Mes</th>
                          <th>Año</th>
                          <th>Monto Multa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {socioDetails.registrosMensuales
                          .filter(r => r.multas > 0)
                          .map((registro, idx) => (
                            <tr key={idx}>
                              <td><strong>{getNombreMes(registro.mes)}</strong></td>
                              <td className="text-center">{registro.año}</td>
                              <td className="text-end text-danger">
                                <strong>{formatCurrency(registro.multas)}</strong>
                              </td>
                            </tr>
                          ))}
                        <tr className="table-light">
                          <td colSpan="2" className="fw-bold">Total Multas:</td>
                          <td className="text-end text-danger fw-bold">
                            {formatCurrency(
                              socioDetails.registrosMensuales.reduce((sum, r) => sum + (r.multas || 0), 0)
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="info">No hay multas registradas</Alert>
                )}
              </Tab>

              {/* Pestaña Préstamos */}
              <Tab eventKey="prestamos" title="💵 Préstamos">
                {socioDetails?.prestamos && socioDetails.prestamos.length > 0 ? (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead className="table-warning">
                        <tr>
                          <th># Solicitud</th>
                          <th>Monto</th>
                          <th>Plazo</th>
                          <th>Estado</th>
                          <th>Fecha Solicitud</th>
                        </tr>
                      </thead>
                      <tbody>
                        {socioDetails.prestamos.map((prestamo) => (
                          <tr key={prestamo._id}>
                            <td className="fw-bold">{prestamo.numeroSolicitud}</td>
                            <td className="text-end">{formatCurrency(prestamo.monto)}</td>
                            <td className="text-center">{prestamo.plazo} meses</td>
                            <td>
                              <Badge bg={
                                prestamo.estado === 'aprobada' ? 'success' :
                                prestamo.estado === 'pendiente' ? 'warning' :
                                prestamo.estado === 'rechazada' ? 'danger' :
                                'info'
                              }>
                                {prestamo.estado === 'aprobada' ? 'Aprobada' :
                                 prestamo.estado === 'pendiente' ? 'Pendiente' :
                                 prestamo.estado === 'rechazada' ? 'Rechazada' :
                                 prestamo.estado}
                              </Badge>
                            </td>
                            <td>{new Date(prestamo.fechaSolicitud).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="info">No hay préstamos registrados</Alert>
                )}
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Reports;